/* Veggiet Internal Order — password gate + qty cart logic.
   Pure functions exported for Jest; DOM wiring runs only when the
   [data-vg-internal] root exists on the page. Client-side gate is a
   deterrent for internal use, not real security (Locksmith for that). */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.VGInternal = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var UNLOCK_KEY = 'vg_internal_unlocked';

  /* ---------- pure logic ---------- */

  async function verifyPassword(input, storedHash, digest) {
    var trimmed = (input || '').trim();
    if (!trimmed || !storedHash) return false;
    var hex = await digest(trimmed);
    return String(hex).toLowerCase() === String(storedHash).toLowerCase();
  }

  function buildCartItems(entries) {
    var out = [];
    (entries || []).forEach(function (e) {
      var id = Number(e.variantId);
      var qty = Number(e.qty);
      if (Number.isFinite(id) && Number.isFinite(qty) && qty > 0) {
        out.push({ id: id, quantity: qty });
      }
    });
    return out;
  }

  function calcSubtotalCents(entries) {
    return (entries || []).reduce(function (sum, e) {
      var qty = Number(e.qty);
      var cents = Number(e.priceCents);
      if (!Number.isFinite(qty) || !Number.isFinite(cents) || qty <= 0) return sum;
      return sum + qty * cents;
    }, 0);
  }

  function formatMoney(cents) {
    var n = Number(cents) || 0;
    return '$' + (n / 100).toFixed(2);
  }

  function buildNotePayload(note) {
    var trimmed = (note || '').trim();
    if (!trimmed) return null;
    return { note: trimmed.slice(0, 1000) };
  }

  function isUnlocked(storage) {
    try { return storage.getItem(UNLOCK_KEY) === '1'; } catch (e) { return false; }
  }

  function setUnlocked(storage) {
    try { storage.setItem(UNLOCK_KEY, '1'); } catch (e) { /* private mode — session-only gate */ }
  }

  /* ---------- browser wiring ---------- */

  function webDigest(str) {
    var bytes = new TextEncoder().encode(str);
    return crypto.subtle.digest('SHA-256', bytes).then(function (buf) {
      return Array.from(new Uint8Array(buf)).map(function (b) {
        return b.toString(16).padStart(2, '0');
      }).join('');
    });
  }

  function readEntries(rootEl) {
    return Array.from(rootEl.querySelectorAll('[data-qty]')).map(function (input) {
      return {
        variantId: input.getAttribute('data-variant'),
        qty: input.value,
        priceCents: input.getAttribute('data-price-cents'),
      };
    });
  }

  function initDom() {
    var rootEl = document.querySelector('[data-vg-internal]');
    if (!rootEl) return;

    var gate = rootEl.querySelector('[data-gate]');
    var content = rootEl.querySelector('[data-content]');
    var input = rootEl.querySelector('[data-gate-input]');
    var err = rootEl.querySelector('[data-gate-err]');
    var subtotalEl = rootEl.querySelector('[data-subtotal]');
    var addBtn = rootEl.querySelector('[data-add]');
    var storedHash = rootEl.getAttribute('data-hash') || '';

    function unlock() {
      if (gate) gate.hidden = true;
      if (content) content.hidden = false;
    }

    function refreshSubtotal() {
      if (subtotalEl) subtotalEl.textContent = formatMoney(calcSubtotalCents(readEntries(rootEl)));
    }

    async function tryUnlock() {
      var ok = await verifyPassword(input ? input.value : '', storedHash, webDigest);
      if (ok) {
        setUnlocked(sessionStorage);
        if (err) err.hidden = true;
        unlock();
        refreshSubtotal();
      } else if (err) {
        err.hidden = false;
      }
    }

    if (isUnlocked(sessionStorage)) unlock();

    var gateBtn = rootEl.querySelector('[data-gate-btn]');
    if (gateBtn) gateBtn.addEventListener('click', tryUnlock);
    if (input) input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') tryUnlock();
    });

    rootEl.addEventListener('input', function (e) {
      if (e.target && e.target.hasAttribute('data-qty')) refreshSubtotal();
    });
    rootEl.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-step]') : null;
      if (!btn) return;
      var qtyInput = btn.parentElement.querySelector('[data-qty]');
      if (!qtyInput) return;
      var next = (Number(qtyInput.value) || 0) + (btn.getAttribute('data-step') === 'up' ? 1 : -1);
      qtyInput.value = Math.max(0, next);
      refreshSubtotal();
    });

    if (addBtn) addBtn.addEventListener('click', function () {
      var items = buildCartItems(readEntries(rootEl));
      if (!items.length) return;
      addBtn.disabled = true;
      var noteEl = rootEl.querySelector('[data-note]');
      var payload = buildNotePayload(noteEl ? noteEl.value : '');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      }).then(function (r) {
        if (!r.ok) throw new Error('cart add failed: ' + r.status);
        if (!payload) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).then(function (u) {
          if (!u.ok) throw new Error('cart note update failed: ' + u.status);
        });
      }).then(function () {
        window.location.href = '/cart';
      }).catch(function (e) {
        addBtn.disabled = false;
        var fail = rootEl.querySelector('[data-add-err]');
        if (fail) fail.hidden = false;
        if (typeof console !== 'undefined') console.error('[vg-internal]', e);
      });
    });

    refreshSubtotal();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initDom);
    } else {
      initDom();
    }
  }

  return {
    verifyPassword: verifyPassword,
    buildCartItems: buildCartItems,
    buildNotePayload: buildNotePayload,
    calcSubtotalCents: calcSubtotalCents,
    formatMoney: formatMoney,
    isUnlocked: isUnlocked,
    setUnlocked: setUnlocked,
  };
});
